<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { ROUTES } from '@/router/routeNames'
import { useAuthStore } from '@/stores/authStore'
import { storeToRefs } from 'pinia'
import { USER_ROLES } from '@/types'
import mixpanel from '@/services/mixpanel'
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)

const props = defineProps({
  isMobileOpen: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])

const route = useRoute()

const navItems = ref([
  {
    name: 'Orders',
    icon: 'pi-shopping-cart',
    route: { name: ROUTES.DASHBOARD_ORDERS },
    active: computed(() => route.name && route.path.includes('orders')),
  },

])

if (user.value?.role === USER_ROLES.CUSTOMER || user.value?.role === USER_ROLES.ADMIN) {
  navItems.value.unshift({
    name: 'Businesses',
    icon: 'pi-building',
    route: { name: ROUTES.BUSINESSES },
    active: computed(() => route.name && route.path.includes('businesses')),
  })
}
if (user.value?.role === USER_ROLES.ADMIN) {
  navItems.value.unshift({
    name: 'Constants',
    icon: 'pi-cog',
    route: { name: ROUTES.ADMIN_CONSTANTS },
    active: computed(() => route.name === ROUTES.ADMIN_CONSTANTS),
  })
  navItems.value.unshift({
    name: 'Send Notifications',
    icon: 'pi-bell',
    route: { name: ROUTES.ADMIN_SEND_NOTIFICATIONS },
    active: computed(() => route.name === ROUTES.ADMIN_SEND_NOTIFICATIONS),
  })
  navItems.value.unshift({
    name: 'Users',
    icon: 'pi-users',
    route: { name: ROUTES.ADMIN_USERS },
    active: computed(() => route.name === ROUTES.ADMIN_USERS),
  })
  navItems.value.unshift({
    name: 'Payments',
    icon: 'pi-wallet',
    route: { name: ROUTES.ADMIN_PAYMENTS },
    active: computed(() => route.path.includes('payments')),
  })
  navItems.value.unshift({
    name: 'Feedbacks',
    icon: 'pi-star',
    route: { name: ROUTES.FEEDBACKS },
    active: computed(() => route.path.includes('feedbacks')),
  })
}
if (user.value?.role === USER_ROLES.BUSINESS) {
  navItems.value.unshift({
    name: 'Products',
    icon: 'pi-shopping-bag',
    route: { name: ROUTES.PRODUCTS },
    active: computed(() => route.name && route.path.includes('products')),
  })
  navItems.value.unshift({
    name: 'Reviews',
    icon: 'pi-star',
    route: { name: ROUTES.BUSINESS_REVIEW, params: { id: user.value.business.uuid} },
    active: computed(() => route.name && route.path.includes('reviews')),
  })
}
navItems.value.unshift({
    name: 'Dashboard',
    icon: 'pi-wave-pulse',
    route: { name: ROUTES.DASHBOARD },
    active: computed(() => route.name === ROUTES.DASHBOARD),
})
navItems.value.unshift({
    name: 'Home',
    icon: 'pi-home',
    route: { name: ROUTES.HOME },
    active: computed(() => route.name === ROUTES.HOME),
})

navItems.value.push({
    name: 'Settings',
    icon: 'pi-cog',
    route: { name: ROUTES.PROFILE },
    active: computed(() => route.name === ROUTES.PROFILE),
})
// Create a logout item with a click handler instead of a route
const isLoggingOut = ref(false)
const handleLogout = async () => {
  if (isLoggingOut.value) return // Prevent multiple clicks

  isLoggingOut.value = true
  try {
    // Use the authStore directly for a more reliable logout
    const authStore = useAuthStore()
    await authStore.logout()
    // The redirect is handled in the authStore
  } catch (error) {
    console.error('Logout failed:', error)
    // Fallback to route-based logout if direct logout fails
    router.push({name: ROUTES.LOGOUT})
  } finally {
    isLoggingOut.value = false
  }
}

navItems.value.push({
    name: 'Logout',
    icon: 'pi-sign-out',
    onClick: handleLogout,
    active: computed(() => false), // Logout is never active
})

const closeSidebar = () => {
  emit('close')
}
</script>

<template>
  <div>
    <!-- Mobile Sidebar Backdrop -->
    <div
      v-if="isMobileOpen"
      class="flex fixed inset-0 z-[9998] flex-col transition-opacity bg-black/50 lg:hidden"
      @click="closeSidebar"
    ></div>

    <!-- Sidebar -->
    <div
      class="fixed top-0 left-0 z-[9999] w-64 h-full bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out transform dark:bg-gray-800 dark:border-gray-700 lg:translate-x-0"
      :class="{
        '-translate-x-full': !isMobileOpen && !$slots.default,
        'lg:translate-x-0': !$slots.default,
        'translate-x-0': isMobileOpen,
      }"
    >
      <!-- Sidebar Header -->
      <div
        class="flex justify-between items-center px-4 h-16 border-b border-gray-200 dark:border-gray-700"
      >
        <div class="flex items-center">
          <img src="/logo.svg" alt="BoxBuddy" class="mr-2 w-8 h-8" />
          <span class="text-lg font-semibold text-gray-800 dark:text-gray-200">BoxBuddy</span>
        </div>
        <button
          @click="closeSidebar"
          class="p-2 text-gray-500 rounded-md lg:hidden hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-300"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>

      <!-- Navigation Links -->
      <nav class="overflow-y-auto pb-32 flex-1 px-2 mt-5 h-[calc(100vh-64px)]">
        <div v-for="item in navItems" :key="item.name" @click="mixpanel.track('Sidebar Item Clicked', {item: item.name, item_route: item.route})">
          <!-- Use RouterLink for items with routes -->
          <RouterLink
            v-if="item.route"
            :to="item.route"
            @click="closeSidebar"
            class="flex items-center px-4 py-3 mb-1 text-base font-medium rounded-md transition-colors"
            :class="[
              item.active
                ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100',
              item.icon === 'pi-sign-out'
                ? 'text-red-700 dark:text-red-600'
                : 'text-gray-700 dark:text-gray-300',
            ]"
          >
            <i
              :class="[
                'pi',
                item.icon,
                'mr-3 text-lg',
                item.active
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-gray-700 dark:text-gray-300',
              ]"
            ></i>
            {{ item.name }}
          </RouterLink>

          <!-- Use button for items with click handlers -->
          <button
            v-else-if="item.onClick"
            @click="item.onClick"
            class="flex items-center px-4 py-3 mb-1 w-full text-base font-medium text-left rounded-md transition-colors"
            :class="[
              'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100',
              item.icon === 'pi-sign-out'
                ? 'text-red-700 dark:text-red-600'
                : 'text-gray-700 dark:text-gray-300',
            ]"
          >
            <i
              :class="[
                'pi',
                item.icon,
                'mr-3 text-lg',
                item.active
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-gray-700 dark:text-gray-300',
              ]"
            ></i>
            <span :class="{'opacity-50': isLoggingOut && item.name === 'Logout'}">{{ item.name }}</span>
            <span v-if="isLoggingOut && item.name === 'Logout'" class="inline-block ml-2 animate-spin">⟳</span>
          </button>
        </div>
      </nav>

      <!-- User Section at Bottom -->
      <div
        class="absolute right-0 bottom-0 left-0 z-50 p-4 bg-gray-100 border-t border-gray-200 dark:bg-gray-900 dark:border-gray-700"
      >
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <div
              class="flex justify-center items-center w-10 h-10 text-lg font-semibold text-green-700 bg-green-100 rounded-full dark:bg-green-900 dark:text-green-300"
            >
              {{ user?.name?.charAt(0).toUpperCase() || 'U' }}
            </div>
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ user?.name || 'User' }}
            </p>
            <RouterLink
              :to="{ name: 'profile' }"
              class="text-xs font-medium text-green-600 hover:text-green-500 dark:text-green-400 dark:hover:text-green-300"
            >
              View profile
            </RouterLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom scrollbar for sidebar */
::-webkit-scrollbar {
  width: 4px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 20px;
}

::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.8);
}
</style>
